import { naechsteDrehung, verschiebe, type SeitenEintrag } from "./seitenOps";

/**
 * Zustand des Seiten-Organisators als Reducer — Liste, Auswahl und
 * Rückgängig-Verlauf ändern sich immer gemeinsam, und die asynchron
 * eintreffenden Einfüge-Ergebnisse müssen auf den AKTUELLEN Stand wirken,
 * nicht auf den beim Klick gesehenen.
 */
export interface SeitenZustand {
    eintraege: SeitenEintrag[];
    /** Frühere Stände für Rückgängig; leer = nichts geändert. */
    verlauf: SeitenEintrag[][];
    auswahl: ReadonlySet<string>;
    /** Ausgangspunkt für Umschalt+Klick. */
    anker: string | null;
}

export type SeitenAktion =
    | { typ: "start"; eintraege: SeitenEintrag[] }
    | { typ: "waehlen"; key: string; art: "einzeln" | "dazu" | "bereich" }
    | { typ: "entfernen"; keys: readonly string[] }
    | { typ: "drehen"; keys: readonly string[] }
    | { typ: "verschieben"; von: number; nach: number }
    /** Hinter die letzte gewählte Seite, sonst ans Ende; danach sind genau die neuen gewählt. */
    | { typ: "einfuegen"; neue: SeitenEintrag[] }
    | { typ: "rueckgaengig" };

export const leererZustand: SeitenZustand = { eintraege: [], verlauf: [], auswahl: new Set(), anker: null };

/** Neuer Stand mit Verlaufseintrag; die Auswahl behält nur noch vorhandene Seiten. */
function mitAenderung(z: SeitenZustand, eintraege: SeitenEintrag[], auswahl?: ReadonlySet<string>): SeitenZustand {
    const da = new Set(eintraege.map((e) => e.key));
    const neueAuswahl = new Set([...(auswahl ?? z.auswahl)].filter((k) => da.has(k)));
    return {
        eintraege,
        verlauf: [...z.verlauf, z.eintraege],
        auswahl: neueAuswahl,
        anker: z.anker && da.has(z.anker) ? z.anker : null,
    };
}

export function seitenReducer(z: SeitenZustand, a: SeitenAktion): SeitenZustand {
    switch (a.typ) {
        case "start":
            return { ...leererZustand, eintraege: a.eintraege };
        case "waehlen": {
            if (a.art === "dazu") {
                const auswahl = new Set(z.auswahl);
                if (auswahl.has(a.key)) auswahl.delete(a.key);
                else auswahl.add(a.key);
                return { ...z, auswahl, anker: a.key };
            }
            if (a.art === "bereich" && z.anker) {
                const keys = z.eintraege.map((e) => e.key);
                const von = keys.indexOf(z.anker);
                const bis = keys.indexOf(a.key);
                if (von >= 0 && bis >= 0) {
                    const [lo, hi] = von < bis ? [von, bis] : [bis, von];
                    // Der Anker bleibt — ein zweiter Umschalt+Klick korrigiert den Bereich.
                    return { ...z, auswahl: new Set(keys.slice(lo, hi + 1)) };
                }
            }
            return { ...z, auswahl: new Set([a.key]), anker: a.key };
        }
        case "entfernen": {
            const weg = new Set(a.keys);
            const rest = z.eintraege.filter((e) => !weg.has(e.key));
            // Die letzte Seite bleibt immer — ein leeres PDF gibt es nicht.
            if (rest.length === 0 || rest.length === z.eintraege.length) return z;
            return mitAenderung(z, rest);
        }
        case "drehen": {
            const ziel = new Set(a.keys);
            if (!z.eintraege.some((e) => ziel.has(e.key))) return z;
            return mitAenderung(
                z,
                z.eintraege.map((e) => (ziel.has(e.key) ? { ...e, drehung: naechsteDrehung(e.drehung) } : e)),
            );
        }
        case "verschieben":
            if (a.von === a.nach || a.von < 0 || a.nach < 0) return z;
            return mitAenderung(z, verschiebe(z.eintraege, a.von, a.nach));
        case "einfuegen": {
            if (a.neue.length === 0) return z;
            let pos = z.eintraege.length;
            for (let i = z.eintraege.length - 1; i >= 0; i--) {
                if (z.auswahl.has(z.eintraege[i].key)) {
                    pos = i + 1;
                    break;
                }
            }
            const eintraege = [...z.eintraege.slice(0, pos), ...a.neue, ...z.eintraege.slice(pos)];
            // So lassen sich unerwünschte Seiten gleich mit Entf wieder wegnehmen.
            const neu = mitAenderung(z, eintraege, new Set(a.neue.map((e) => e.key)));
            return { ...neu, anker: a.neue[0].key };
        }
        case "rueckgaengig": {
            if (z.verlauf.length === 0) return z;
            const eintraege = z.verlauf[z.verlauf.length - 1];
            const da = new Set(eintraege.map((e) => e.key));
            return {
                eintraege,
                verlauf: z.verlauf.slice(0, -1),
                auswahl: new Set([...z.auswahl].filter((k) => da.has(k))),
                anker: z.anker && da.has(z.anker) ? z.anker : null,
            };
        }
    }
}
